/**
 * Pure helpers for keeping the catalogue's state Maps in step with live
 * Firestore listeners WITHOUT replacing the Map objects.
 *
 * ☠️ Why in place: `loaders/ensure.ts` documents the invariant — a plan load
 * captures `state.catalogueData` at its start, so swapping in a new Map
 * mid-load strands whatever it adds in the old one. The OMON-102 listeners
 * used to assign fresh Maps on every snapshot; they now go through these.
 *
 * No Firebase imports here, so the logic is unit-testable without a mock.
 */

/** One keyed change derived from a snapshot's `docChanges()`. */
export type LiveChange<V> =
  | { type: "upsert"; key: string; value: V }
  | { type: "remove"; key: string };

/** Make `target` hold exactly `source`'s entries, keeping the same Map object. */
export function fillInPlace<K, V>(target: Map<K, V>, source: Map<K, V>): void {
  target.clear();
  source.forEach((v, k) => target.set(k, v));
}

/**
 * Apply incremental changes to `target` in place. Returns true if anything was
 * applied (so the caller can skip a render for an empty delta).
 */
export function applyLiveChanges<V>(target: Map<string, V>, changes: LiveChange<V>[]): boolean {
  for (const c of changes) {
    if (c.type === "upsert") target.set(c.key, c.value);
    else target.delete(c.key);
  }
  return changes.length > 0;
}

/**
 * Collapse many calls in one frame into a single `fn()` on the next animation
 * frame (falls back to a 16ms timer where rAF is unavailable, e.g. tests).
 * Four catalogue listeners firing together used to cost four full tree renders.
 */
export function coalesceToFrame(fn: () => void): () => void {
  let queued = false;
  const raf = (globalThis as { requestAnimationFrame?: (cb: () => void) => number })
    .requestAnimationFrame;
  const schedule = typeof raf === "function" ? (cb: () => void) => raf(cb) : (cb: () => void) => setTimeout(cb, 16);
  return () => {
    if (queued) return;
    queued = true;
    schedule(() => {
      queued = false;
      fn();
    });
  };
}
